import deleteIcon from "../../../assets/images/delete.svg"
import { useEffect, useMemo, useState } from "react"

import Swal from "sweetalert2"
import { Dropdown } from "react-bootstrap"
import { AddDocumentsModal } from "./AddDocumentsModal.jsx"
import { useClientDocuments } from "../hooks/useClientDocuments.js"

export function ClientDocuments({ entities }) {
    const {
        assignedDocs,
        availableDocs,
        isLoadingAssigned,
        isLoadingAvailable,
        isAssigning,
        showModal,
        setShowModal,
        addSelectedDocs,
        isDocAssigned,
        removeDoc,
    } = useClientDocuments()
    
    const [selectedEntityId, setSelectedEntityId] = useState(entities?.[0]?._id || "")

    useEffect(() => {
        if (!entities?.length) {
            setSelectedEntityId("")
            return
        }

        setSelectedEntityId((previousEntityId) => {
            const entityExists = entities.some((entity) => entity._id?.toString() === previousEntityId?.toString())
            if (entityExists) return previousEntityId
            return entities[0]._id
        })
    }, [entities])

    const selectedEntity = useMemo(
        () => entities?.find((entity) => entity._id?.toString() === selectedEntityId?.toString()) || entities?.[0],
        [entities, selectedEntityId]
    )

    const filteredAssignedDocs = useMemo(() => {
        if (!selectedEntityId) return []
        return assignedDocs.filter((doc) => doc.entityAssignId?.toString() === selectedEntityId.toString())
    }, [assignedDocs, selectedEntityId])

    const handleDeleteDoc = (doc) => {
        Swal.fire({
            title: "Remove document",
            text: `Are you sure you want to remove "${doc.documentName}"?`,
            icon: "warning",
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: "Yes, remove",
        }).then((result) => {
            if (result.isConfirmed) removeDoc(doc._id)
        })
    }

    return (
        <div className="clientDocumentsSection">
            <button
                className="btnBgBlueTextGradient addDocumentsBtn"
                onClick={() => setShowModal(true)}
            >
                Add documents
            </button>
            
            <div className="clientDocumentsHeader">
                <h5 className="titleHeader">Documents assigned</h5>

                <Dropdown className="clientDocumentsEntityDropdown">
                    <Dropdown.Toggle className={selectedEntityId ? "" : "default"}>
                        {selectedEntity?.entityName || "DEFAULT ENTITY"}
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                        {entities?.map((entity) => (
                            <Dropdown.Item
                                key={entity._id}
                                onClick={() => setSelectedEntityId(entity._id)}
                            >
                                {entity.entityName || "DEFAULT ENTITY"}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            {isLoadingAssigned ? (
                <p className="loadingText">Loading documents...</p>
            ) : (
                <>
                    {/* Desktop table */}
                    <div className="d-none d-lg-block">
                        <div className="docsTableTitles">
                            <div className="docCol"><span className="label">Document name</span></div>
                            <div className="docCol periodicityCol"><span className="label">Periodicity</span></div>
                            <div className="docCol countryCol"><span className="label">Country</span></div>
                            <div className="docCol typeCol"><span className="label">Type of person</span></div>
                            <div className="docCol"><span className="label">Document disclaimer</span></div>
                            <div className="docCol"><span className="label">URL to download</span></div>
                            <div className="docCol actionCol"></div>
                        </div>

                        {filteredAssignedDocs.length === 0 ? (
                            <p className="emptyText">No documents assigned yet.</p>
                        ) : (
                            filteredAssignedDocs.map((doc) => (
                                <div key={doc._id} className="docsTableRow">
                                    <div className="docCol">{doc.documentName}</div>
                                    <div className="docCol periodicityCol">
                                        {doc.periodicity}
                                        {doc.periodicity != null && (
                                            <span className="periodicityUnit"> Month(s)</span>
                                        )}
                                    </div>
                                    <div className="docCol countryCol">{doc.country || "—"}</div>
                                    <div className="docCol typeCol">{doc.typePerson || "—"}</div>
                                    <div className="docCol">{doc.documentDisclaimer || "—"}</div>
                                    <div className="docCol">{doc.urlDownload || "—"}</div>
                                    <div className="docCol actionCol">
                                        <img
                                            className="deleteIcon"
                                            src={deleteIcon}
                                            alt="Remove"
                                            onClick={() => handleDeleteDoc(doc)}
                                        />
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Mobile list */}
                    <div className="d-lg-none clientDocsMobileList">
                        {filteredAssignedDocs.length === 0 ? (
                            <p className="emptyText">No documents assigned yet.</p>
                        ) : (
                            filteredAssignedDocs.map((doc) => (
                                <div key={doc._id} className="clientDocsMobileRow">
                                    <h5 className="clientDocsMobileName">
                                        {doc.documentName}
                                        <img
                                            className="deleteIcon"
                                            src={deleteIcon}
                                            alt="Remove"
                                            onClick={() => handleDeleteDoc(doc)}
                                        />
                                    </h5>
                                    <div className="clientDocsMobileInformation">
                                        <div className="docInfoContainer">
                                            <span className="docValue">
                                                {doc.periodicity != null ? `${doc.periodicity} Month(s)` : "—"}
                                            </span>
                                            <span className="docTitle">Periodicity</span>
                                        </div>
                                        <div className="docInfoContainer">
                                            <span className="docValue">{doc.country || "—"}</span>
                                            <span className="docTitle">Country</span>
                                        </div>
                                        <div className="docInfoContainer">
                                            <span className="docValue">{doc.typePerson || "—"}</span>
                                            <span className="docTitle">Type of person</span>
                                        </div>
                                        {doc.documentDisclaimer && (
                                            <div className="docInfoContainer fullWidthInfo">
                                                <span className="docValue">{doc.documentDisclaimer}</span>
                                                <span className="docTitle">Document disclaimer</span>
                                            </div>
                                        )}
                                        {doc.urlDownload && (
                                            <div className="docInfoContainer fullWidthInfo">
                                                <span className="docValue">{doc.urlDownload}</span>
                                                <span className="docTitle">URL to download</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </>
            )}

            <AddDocumentsModal
                show={showModal}
                onHide={() => setShowModal(false)}
                availableDocs={availableDocs}
                isLoadingAvailable={isLoadingAvailable}
                isDocAssigned={(docId) => isDocAssigned(docId, selectedEntityId)}
                onConfirm={(docs) => addSelectedDocs(docs, selectedEntityId)}
                isAssigning={isAssigning}
                selectedEntityId={selectedEntityId}
            />
        </div>
    )
}
